import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, TextButton } from '@zillow/constellation-10';
import { LoadingParagraph, LoadingTag } from '@zg-rentals/loading-components';
import { useRentalManagerUserPropertySummaryWebApiHandler_payments } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useSelector } from '../../selectors/useSelector';

import { PAYMENT_STATUS_TEXT_TO_TYPE } from '../../constants/PaymentConstants';
import TimeConstants from '../../constants/TimeConstants';
import SelectPropertyConstants from '../../constants/SelectPropertyConstants';

import numberUtils from '../../utils/numberUtils';
import selectPropertyUtils from '../../utils/selectPropertyUtils';

import { getTagTypeByKey, STATUS_KEY } from '../../utils/statusTagUtils';

import ErrorMessagesAlert from '../ErrorMessagesAlert';

import SimpleCard from '../SimpleCard';
import ConnectedModuleCard from '../../components/ConnectedModuleCard';

const { PAYMENT_SUMMARY } = STATUS_KEY;

const PaymentsModule = () => {
  const { listingAlias } = useSelector((state) => {
    return {
      listingAlias: state.properties.currentProperty.listingAlias,
    };
  }, shallowEqual);

  const {
    data = {
      paymentCardList: [{ title: '', amount: 0, paymentTracker: { statusText: '' } }],
    },
    isPending,
    error,
  } = useRentalManagerUserPropertySummaryWebApiHandler_payments(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10 },
  );

  // use listingAlias from currentProperty as this is not returned in the /summary/payments endpoint
  const paymentsPath = selectPropertyUtils.getPathByType(
    SelectPropertyConstants.PAYMENTS as keyof typeof SelectPropertyConstants,
    listingAlias,
  );

  const paymentCardListLength = data?.paymentCardList?.length || 0;

  const renderCTA = useMemo(() => {
    return (
      <TextButton tone="brand" asChild>
        <a href={paymentsPath || ''} target="_self">
          Details
        </a>
      </TextButton>
    );
  }, [paymentsPath]);

  const renderNoPayments = useMemo(() => {
    return (
      <Box>
        <SimpleCard body={'Set up payments'} bodyHeading={'Upcoming payments will appear here'} withChevron={false} />
      </Box>
    );
  }, [isPending]);

  const renderPaymentCardList = (paymentCardItem: {
    title: string;
    amount: number;
    paymentTracker: { statusText: string };
  }) => {
    const {
      title,
      amount,
      paymentTracker: { statusText },
    } = paymentCardItem;
    const displayAmount = numberUtils.asCurrency(amount / 100);

    const toneTypeKey = PAYMENT_STATUS_TEXT_TO_TYPE[statusText.toLowerCase()] || 'neutral';
    const tone = getTagTypeByKey(PAYMENT_SUMMARY, toneTypeKey);

    return (
      <Box key={`PaymentCard-${title}`}>
        <SimpleCard withChevron={false}>
          <LoadingTag
            data-testid="PaymentCard-Status"
            size="sm"
            tone={tone}
            css={{ margin: '0 {spacing.200} {spacing.200} 0' }}
            isLoading={isPending}
            loadingWidth="10%"
          >
            {statusText}
          </LoadingTag>
          <LoadingParagraph
            data-testid="PaymentCard-Title"
            textStyle="body-sm-bold"
            isLoading={isPending}
            loadingWidth="40%"
            css={{ fontSize: { lg: 'body' }, marginBottom: '200' }}
          >
            {title}
          </LoadingParagraph>
          <LoadingParagraph
            data-testid="PaymentCard-Amount"
            textStyle="body-sm"
            isLoading={isPending}
            loadingWidth="20%"
          >
            {displayAmount}
          </LoadingParagraph>
        </SimpleCard>
      </Box>
    );
  };

  return (
    <ConnectedModuleCard
      data-testid="PaymentsModule"
      title="Payments"
      ctaComponent={!error && renderCTA}
      ctaFontType="body-sm"
      justifyHeadingContent={'center'}
    >
      {error ? (
        <ErrorMessagesAlert
          appearance="warning"
          data-testid="PaymentsModuleError"
          errorMessages={
            (error as { error?: { data?: { partialMessages?: Array<{ text: string }> } } })?.error?.data
              ?.partialMessages || [
              { text: (error as { error?: { message?: string } })?.error?.message || 'Sorry, an error occurred.' },
            ]
          }
        />
      ) : (
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: {
              base: 'repeat(1, 1fr)',
              lg: paymentCardListLength < 2 ? 'repeat(1, minmax(0,1fr))' : 'repeat(2, minmax(0,1fr))',
            },
            gap: { base: '200', lg: '400' },
          }}
        >
          {paymentCardListLength > 0 ? data?.paymentCardList.map(renderPaymentCardList) : renderNoPayments}
        </Box>
      )}
    </ConnectedModuleCard>
  );
};

export default PaymentsModule;
